// client/src/components/DistrictStats.jsx
import React, { useState, useMemo } from 'react';
import { Table, Badge, Form, Row, Col, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';

const DistrictStats = ({ allData, filters, onDistrictSelect }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'district', direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');

    // Функция для нормализации даты
    const normalizeDate = (dateValue) => {
        if (!dateValue) return null;
        return String(dateValue).substring(0, 10);
    };

    // Расчет статистики по районам с учетом фильтров
    const districtStats = useMemo(() => {
        if (!allData?.monitoringData || !allData?.districts) return [];

        // Берем список районов из allData.districts (из таблицы schools)
        const allDistricts = allData.districts || [];
        
        let filteredMonitoringData = [...allData.monitoringData];
        
        // Применяем фильтры
        if (filters.startDate || filters.endDate) {
            filteredMonitoringData = filteredMonitoringData.filter(record => {
                if (!record.date) return false;
                const recordDate = normalizeDate(record.date);
                let startOk = true;
                let endOk = true;
                if (filters.startDate) startOk = recordDate >= filters.startDate;
                if (filters.endDate) endOk = recordDate <= filters.endDate;
                return startOk && endOk;
            });
        }
        
        if (filters.role !== 'all') {
            filteredMonitoringData = filteredMonitoringData.filter(d => d.role === filters.role);
        }

        // Группировка данных по районам из мониторинга
        const monitoringStatsMap = new Map();
        
        filteredMonitoringData.forEach(record => {
            if (!record.district) return;
            
            if (!monitoringStatsMap.has(record.district)) {
                monitoringStatsMap.set(record.district, {
                    schools_with_data: new Set(),
                    total_records: 0,
                    total_views: 0,
                    unique_users: new Set(),
                    unique_students: new Set(),
                    unique_teachers: new Set(),
                    subjects: new Set()
                });
            }
            
            const stats = monitoringStatsMap.get(record.district);
            stats.total_records++;
            stats.total_views += record.views_count || 0;
            stats.unique_users.add(record.user_alias);
            if (record.role === 'student') stats.unique_students.add(record.user_alias);
            if (record.role === 'teacher') stats.unique_teachers.add(record.user_alias);
            if (record.subject) stats.subjects.add(record.subject);
            if (record.school_id) stats.schools_with_data.add(record.school_id);
        });

        // Получаем общее количество школ в каждом районе из allData.schools
        const schoolsByDistrict = new Map();
        allData.schools.forEach(school => {
            if (!school.district) return;
            if (!schoolsByDistrict.has(school.district)) {
                schoolsByDistrict.set(school.district, []);
            }
            schoolsByDistrict.get(school.district).push(school);
        });

        // Формируем результат для ВСЕХ районов из справочника
        const result = [];
        for (const district of allDistricts) {
            const monitoringStats = monitoringStatsMap.get(district) || {
                schools_with_data: new Set(),
                total_records: 0,
                total_views: 0,
                unique_users: new Set(),
                unique_students: new Set(),
                unique_teachers: new Set(),
                subjects: new Set()
            };
            
            const totalSchools = schoolsByDistrict.get(district)?.length || 0;
            const schoolsWithDataCount = monitoringStats.schools_with_data.size;
            const coveragePercent = totalSchools > 0 ? (schoolsWithDataCount / totalSchools * 100) : 0;
            
            result.push({
                district: district,
                total_schools: totalSchools,
                schools_with_data: schoolsWithDataCount,
                coverage_percent: parseFloat(coveragePercent.toFixed(1)),
                total_views: monitoringStats.total_views,
                unique_users: monitoringStats.unique_users.size,
                unique_students: monitoringStats.unique_students.size,
                unique_teachers: monitoringStats.unique_teachers.size,
                subjects_count: monitoringStats.subjects.size,
                total_records: monitoringStats.total_records
            });
        }

        return result.sort((a, b) => a.district.localeCompare(b.district));
    }, [allData, filters]);

    // Фильтрация по поиску
    const filteredStats = useMemo(() => {
        if (!searchTerm) return districtStats;
        const term = searchTerm.toLowerCase();
        return districtStats.filter(stat => 
            stat.district.toLowerCase().includes(term)
        );
    }, [districtStats, searchTerm]);

    // Сортировка
    const sortedStats = useMemo(() => {
        return [...filteredStats].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];
            
            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (sortConfig.direction === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });
    }, [filteredStats, sortConfig]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const getSortClass = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'asc' ? 'asc' : 'desc';
        }
        return '';
    };

    const exportToExcel = () => {
        const exportData = sortedStats.map(stat => ({
            'Район': stat.district,
            'Всего ОУ': stat.total_schools,
            'ОУ с данными': stat.schools_with_data,
            'Охват (%)': stat.coverage_percent,
            'Просмотры': stat.total_views,
            'Пользователи': stat.unique_users,
            'Ученики': stat.unique_students,
            'Учителя': stat.unique_teachers,
            'Предметы': stat.subjects_count,
            'Записей': stat.total_records
        }));
        
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Статистика по районам');
        
        // Автоподбор ширины колонок
        const wscols = [
            { wch: 25 }, // Район
            { wch: 12 }, // Всего ОУ
            { wch: 14 }, // ОУ с данными
            { wch: 12 }, // Охват (%)
            { wch: 14 }, // Просмотры
            { wch: 14 }, // Пользователи
            { wch: 12 }, // Ученики
            { wch: 12 }, // Учителя
            { wch: 12 }, // Предметы
            { wch: 12 }  // Записей
        ];
        ws['!cols'] = wscols;
        
        const fileName = `ub_cok_districts_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const getCoverageColor = (percent) => {
        if (percent >= 80) return '#198754';
        if (percent >= 50) return '#ffc107';
        return '#dc3545';
    };

    // Подсчет итогов
    const totals = useMemo(() => {
        return sortedStats.reduce((acc, stat) => {
            acc.total_schools += stat.total_schools;
            acc.schools_with_data += stat.schools_with_data;
            acc.total_views += stat.total_views;
            acc.unique_users += stat.unique_users;
            acc.unique_students += stat.unique_students;
            acc.unique_teachers += stat.unique_teachers;
            acc.subjects_count += stat.subjects_count;
            acc.total_records += stat.total_records;
            return acc;
        }, {
            total_schools: 0,
            schools_with_data: 0,
            total_views: 0,
            unique_users: 0,
            unique_students: 0,
            unique_teachers: 0,
            subjects_count: 0,
            total_records: 0
        });
        
        const totalCoverage = totals.total_schools > 0 
            ? (totals.schools_with_data / totals.total_schools * 100).toFixed(1)
            : 0;
        
        return { ...totals, totalCoverage };
    }, [sortedStats]);

    return (
        <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
                <div>
                    <i className="bi bi-map me-2"></i>
                    Статистика по районам
                    <Badge bg="secondary" className="ms-2">{sortedStats.length}</Badge>
                </div>
                <div className="d-flex gap-2">
                    <div className="input-group" style={{ width: '250px' }}>
                        <span className="input-group-text"><i className="bi bi-search"></i></span>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Поиск района..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="export-btn" onClick={exportToExcel}>
                        <i className="bi bi-file-excel me-1"></i> Экспорт XLSX
                    </button>
                </div>
            </div>
            <div className="table-container">
                <Table hover responsive className="mb-0">
                    <thead>
                        <tr>
                            <th className={`sortable ${getSortClass('district')}`} onClick={() => handleSort('district')}>Район</th>
                            <th className={`sortable text-center ${getSortClass('total_schools')}`} onClick={() => handleSort('total_schools')}>Всего ОУ</th>
                            <th className={`sortable text-center ${getSortClass('schools_with_data')}`} onClick={() => handleSort('schools_with_data')}>ОУ с данными</th>
                            <th className={`sortable text-center ${getSortClass('coverage_percent')}`} onClick={() => handleSort('coverage_percent')}>Охват (%)</th>
                            <th className={`sortable text-end ${getSortClass('total_views')}`} onClick={() => handleSort('total_views')}>Просмотры</th>
                            <th className={`sortable text-end ${getSortClass('unique_users')}`} onClick={() => handleSort('unique_users')}>Пользователи</th>
                            <th className={`sortable text-end ${getSortClass('unique_students')}`} onClick={() => handleSort('unique_students')}>Ученики</th>
                            <th className={`sortable text-end ${getSortClass('unique_teachers')}`} onClick={() => handleSort('unique_teachers')}>Учителя</th>
                            <th className={`sortable text-end ${getSortClass('subjects_count')}`} onClick={() => handleSort('subjects_count')}>Предметы</th>
                        </tr>
                        </thead>
                        <tbody>
                            {sortedStats.map((stat) => (
                                <tr 
                                    key={stat.district} 
                                    className="district-row"
                                    onClick={() => onDistrictSelect && onDistrictSelect(stat.district)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>
                                        <strong>{stat.district}</strong>
                                        {stat.schools_with_data === 0 && (
                                            <Badge bg="secondary" className="ms-2">нет данных</Badge>
                                        )}
                                    </td>
                                    <td className="text-center">{stat.total_schools.toLocaleString()}</td>
                                    <td className="text-center">
                                        <span className={`badge ${stat.schools_with_data > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                            {stat.schools_with_data.toLocaleString()}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex align-items-center gap-2">
                                            <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                                <div 
                                                    className="progress-bar" 
                                                    style={{ 
                                                        width: `${stat.coverage_percent}%`,
                                                        backgroundColor: getCoverageColor(stat.coverage_percent)
                                                    }}
                                                ></div>
                                            </div>
                                            <span style={{ minWidth: '45px' }}>{stat.coverage_percent.toFixed(1)}%</span>
                                        </div>
                                    </td>
                                    <td className="text-end">{stat.total_views.toLocaleString()}</td>
                                    <td className="text-end">{stat.unique_users.toLocaleString()}</td>
                                    <td className="text-end">{stat.unique_students.toLocaleString()}</td>
                                    <td className="text-end">{stat.unique_teachers.toLocaleString()}</td>
                                    <td className="text-end">{stat.subjects_count.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="table-light">
                            <tr>
                                <td className="fw-bold">ИТОГО:</td>
                                <td className="text-center fw-bold">{totals.total_schools.toLocaleString()}</td>
                                <td className="text-center fw-bold">{totals.schools_with_data.toLocaleString()}</td>
                                <td className="text-center fw-bold">{totals.totalCoverage}%</td>
                                <td className="text-end fw-bold">{totals.total_views.toLocaleString()}</td>
                                <td className="text-end fw-bold">{totals.unique_users.toLocaleString()}</td>
                                <td className="text-end fw-bold">{totals.unique_students.toLocaleString()}</td>
                                <td className="text-end fw-bold">{totals.unique_teachers.toLocaleString()}</td>
                                <td className="text-end fw-bold">{totals.subjects_count.toLocaleString()}</td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
                <div className="card-footer text-muted small">
                    <i className="bi bi-info-circle me-1"></i>
                    Данные учитывают выбранные фильтры (дата, роль)
                    {searchTerm && <span className="ms-2">• Найдено районов: {sortedStats.length}</span>}
                </div>
            </div>
        );
    };

export default DistrictStats;